import React, { type CSSProperties, type PropsWithChildren } from "react";

import clsx from "clsx";
import {
  type Control,
  type UseFormRegister,
  useFormState,
} from "react-hook-form";

import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { useFeatureFlags } from "@hooks/http";
import {
  type BenchmarkPlatformEnum,
  BenchmarkTypeEnum,
} from "@north-beam/nb-client-side";
import { AdIcon } from "@pages/objects/utils";

import { BenchmarkPlatformFriendlyNames } from "../constants";
import { getPrettyBenchmark } from "../utils";

import {
  type BenchmarksByPlatform,
  type PlatformBenchmarks,
} from "./custom-benchmarks-modal";
import { NoDataNotification } from "./notification";

const TableCell = ({
  children,
  className,
  textAlign = "left",
}: PropsWithChildren<{
  className?: string;
  textAlign?: CSSProperties["textAlign"];
}>) => (
  <div className={clsx("table-cell relative", className)} style={{ textAlign }}>
    {children}
  </div>
);
export const HeaderTableCell = ({
  children,
  missingBorder,
}: PropsWithChildren<{ missingBorder?: boolean }>) => (
  <TableCell
    className={clsx(
      "font-semibold rounded-lg bg-[#FCFCFF] py-1 px-2",
      missingBorder ? "" : "border-r",
    )}
  >
    {children}
  </TableCell>
);
export const BodyTableCell = ({
  children,
  missingBorder,
  className,
  textAlign,
}: PropsWithChildren<{
  missingBorder?: boolean;
  className?: string;
  textAlign?: CSSProperties["textAlign"];
}>) => (
  <TableCell
    className={clsx(
      "py-1 px-2",
      missingBorder ? "" : "border-r items-start",
      className,
    )}
    textAlign={textAlign}
  >
    {children}
  </TableCell>
);

type TableCellInput = {
  missingBorder?: boolean;
  hasError?: boolean;
  cellRegistration: Parameters<UseFormRegister<BenchmarksByPlatform>>[0];
  register: UseFormRegister<BenchmarksByPlatform>;
  inputClassname?: string;
};

export const CacBodyTableCellInput = ({
  missingBorder,
  hasError,
  cellRegistration,
  register,
}: TableCellInput) => {
  return (
    <TableCell className={clsx(missingBorder ? "" : "border-r ")}>
      <div className="left-2 top-1 absolute">$</div>
      <input
        {...register(cellRegistration)}
        className={clsx(
          "w-full pl-6 pr-2 h-full text-right text-mono focus:border focus:outline-none",
          "focus:border-primary focus:ring-1 focus:ring-primary",
          hasError ? "bg-error placeholder:text-black" : "",
        )}
        placeholder="--.-"
        autoComplete="off"
      />
    </TableCell>
  );
};

export const RoasBodyTableCellInput = ({
  missingBorder,
  hasError,
  register,
  cellRegistration,
  inputClassname,
}: TableCellInput) => (
  <TableCell className={clsx(missingBorder ? "" : "border-r ")}>
    <input
      {...register(cellRegistration)}
      className={clsx(
        "w-full pl-6 pr-2 h-full text-right text-mono focus:border focus:outline-none",
        "focus:border-primary focus:ring-1 focus:ring-primary",
        hasError ? "bg-error placeholder:text-black" : "",
        inputClassname,
      )}
      placeholder="--.-"
      autoComplete="off"
    />
  </TableCell>
);

export type BenchmarkReviewCardProps = {
  platform: BenchmarkPlatformEnum;
  cac: PlatformBenchmarks["cac"];
  roas: PlatformBenchmarks["roas"];
  label: string | null;
  loading: boolean;
  ignoreNotEnoughData?: boolean;
  neverGreyOut?: boolean;
  benchmarkType: BenchmarkTypeEnum;
  benchmarkTarget: number | null;
};

export const BenchmarkReviewCardSkeleton = ({
  platform,
  cac,
  roas,
  label,
  loading,
  ignoreNotEnoughData,
  neverGreyOut,
  children,
  benchmarkTarget,
  benchmarkType,
}: PropsWithChildren<BenchmarkReviewCardProps>) => {
  const { hasFeatureFlag } = useFeatureFlags();
  const { user } = useUser();

  const noData =
    !loading &&
    !cac?.blended &&
    !cac?.returning &&
    !cac?.firstTime &&
    !roas?.blended &&
    !roas?.returning &&
    !roas?.firstTime;

  return (
    <div className="relative flex flex-col">
      {!loading && noData && !ignoreNotEnoughData && (
        <NoDataNotification
          placement="topLeft"
          className="z-[2002] mt-[-1.3rem] ml-1"
        />
      )}
      {label && (
        <div className="text-end mb-1">
          <span className="rounded-lg p-2 text-xs bg-cosmic-4 text-primary">
            {label}
          </span>
        </div>
      )}
      <div
        className={clsx(
          { "opacity-25": !loading && noData && !neverGreyOut },
          "flex my-2 items-center border rounded-lg relative grow",
        )}
      >
        <div className="flex flex-col items-center justify-center h-full border-r w-40">
          <AdIcon sizeInEM={2} platform={platform} style={{ marginRight: 0 }} />
          <div className="font-bold mt-1.5">
            {BenchmarkPlatformFriendlyNames[platform]}
          </div>
          <div className="text-xs truncate max-w-[11rem] px-3">
            {benchmarkType} Target:{" "}
            {getPrettyBenchmark(benchmarkType, benchmarkTarget, user)}
          </div>
        </div>
        <div
          className={clsx(
            "table table-fixed border-collapse w-full mb-0 h-full grow",
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

const Headers = () => (
  <div className="table-header-group rounded-lg  ">
    <div className="table-row border-b">
      <HeaderTableCell>
        Metric{" "}
        <GenericTooltip content="The unit for measuring your performance over time" />
      </HeaderTableCell>
      <HeaderTableCell>
        Blended{" "}
        <GenericTooltip content="Weighted average of first-time and returning attribution" />
      </HeaderTableCell>
      <HeaderTableCell>
        First Time{" "}
        <GenericTooltip content="Measurement for first-time customer attribution" />
      </HeaderTableCell>
      <HeaderTableCell missingBorder>
        Returning{" "}
        <GenericTooltip content="Measurement for returning customer attribution" />
      </HeaderTableCell>
    </div>
  </div>
);

export const BenchmarkReviewCard = (props: BenchmarkReviewCardProps) => {
  const { cac, roas } = props;
  const { user } = useUser();
  return (
    <BenchmarkReviewCardSkeleton {...props}>
      <Headers />
      <div className="table-row-group border-b">
        <BodyTableCell>CAC</BodyTableCell>
        <BodyTableCell textAlign="right" className="font-mono">
          {getPrettyBenchmark(BenchmarkTypeEnum.CAC, cac?.blended, user)}
        </BodyTableCell>
        <BodyTableCell textAlign="right" className="font-mono">
          {getPrettyBenchmark(BenchmarkTypeEnum.CAC, cac?.firstTime, user)}
        </BodyTableCell>
        <BodyTableCell missingBorder textAlign="right" className="font-mono">
          {getPrettyBenchmark(BenchmarkTypeEnum.CAC, cac?.returning, user)}
        </BodyTableCell>
      </div>
      <div className="table-row-group">
        <BodyTableCell>ROAS</BodyTableCell>
        <BodyTableCell textAlign="right" className="font-mono">
          {getPrettyBenchmark(BenchmarkTypeEnum.ROAS, roas?.blended, user)}
        </BodyTableCell>
        <BodyTableCell textAlign="right" className="font-mono">
          {getPrettyBenchmark(BenchmarkTypeEnum.ROAS, roas?.firstTime, user)}
        </BodyTableCell>
        <BodyTableCell missingBorder textAlign="right" className="font-mono">
          {getPrettyBenchmark(BenchmarkTypeEnum.ROAS, roas?.returning, user)}
        </BodyTableCell>
      </div>
    </BenchmarkReviewCardSkeleton>
  );
};

export const EditableBenchmarkReviewCard = ({
  control,
  register,
  index,
  ...otherProps
}: Omit<BenchmarkReviewCardProps, "fake"> & {
  control: Control<BenchmarksByPlatform>;
  register: UseFormRegister<BenchmarksByPlatform>;
  index: number;
}) => {
  const { platform } = otherProps;

  const { errors } = useFormState({ control });

  return (
    <BenchmarkReviewCardSkeleton {...otherProps}>
      <Headers />
      <div className="table-row-group border-b">
        <BodyTableCell>CAC</BodyTableCell>
        <CacBodyTableCellInput
          hasError={!!errors?.[platform]?.[index]?.cac?.blended}
          cellRegistration={`${platform}.${index}.cac.blended`}
          register={register}
        />
        <CacBodyTableCellInput
          hasError={!!errors?.[platform]?.[index]?.cac?.firstTime}
          register={register}
          cellRegistration={`${platform}.${index}.cac.firstTime`}
        />
        <CacBodyTableCellInput
          missingBorder
          hasError={!!errors?.[platform]?.[index]?.cac?.returning}
          register={register}
          cellRegistration={`${platform}.${index}.cac.returning`}
        />
      </div>
      <div className="table-row-group">
        <BodyTableCell>ROAS</BodyTableCell>
        <RoasBodyTableCellInput
          hasError={!!errors?.[platform]?.[index]?.roas?.blended}
          register={register}
          cellRegistration={`${platform}.${index}.roas.blended`}
        />
        <RoasBodyTableCellInput
          hasError={!!errors?.[platform]?.[index]?.roas?.firstTime}
          register={register}
          cellRegistration={`${platform}.${index}.roas.firstTime`}
        />
        <RoasBodyTableCellInput
          missingBorder
          hasError={!!errors?.[platform]?.[index]?.roas?.returning}
          register={register}
          cellRegistration={`${platform}.${index}.roas.returning`}
          inputClassname="rounded-br-[0.8rem]"
        />
      </div>
    </BenchmarkReviewCardSkeleton>
  );
};
